import { getAccessToken, isTokenExpired, refreshAccessToken, logout } from './auth';
import { jsonRequestInit } from './utils/jsonRequest';
import { withViewAs } from './utils/viewAs';

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export function extractErrorMessage(data: unknown, fallback?: string): string {
  if (data === null || data === undefined) {
    return fallback ?? 'Unknown error';
  }
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) return fallback ?? 'Unknown error';
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return extractErrorMessage(parsed, fallback || trimmed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  if (data instanceof Error) {
    return extractErrorMessage(data.message, fallback);
  }
  if (Array.isArray(data)) {
    const messages = data
      .map(item => extractErrorMessage(item, ''))
      .filter(m => m.length > 0);
    if (messages.length > 0) {
      return messages.join('; ');
    }
    return fallback ?? 'Unknown error';
  }
  if (typeof data === 'object') {
    const rec = data as Record<string, unknown>;
    if (typeof rec.detail === 'string' && rec.detail.trim()) {
      return rec.detail.trim();
    }
    if (rec.detail !== undefined && rec.detail !== null) {
      const detailMsg = extractErrorMessage(rec.detail, '');
      if (detailMsg) return detailMsg;
    }
    if (typeof rec.message === 'string' && rec.message.trim()) {
      return rec.message.trim();
    }
    if (rec.message !== undefined && rec.message !== null) {
      const msg = extractErrorMessage(rec.message, '');
      if (msg) return msg;
    }
    if (typeof rec.error === 'string' && rec.error.trim()) {
      return rec.error.trim();
    }
    if (rec.error !== undefined && rec.error !== null) {
      const errMsg = extractErrorMessage(rec.error, '');
      if (errMsg) return errMsg;
    }
    if (Array.isArray(rec.errors) && rec.errors.length > 0) {
      const errorsMsg = extractErrorMessage(rec.errors, '');
      if (errorsMsg) return errorsMsg;
    }
    if (typeof rec.msg === 'string' && rec.msg.trim()) {
      return rec.msg.trim();
    }
    if (typeof rec.reason === 'string' && rec.reason.trim()) {
      return rec.reason.trim();
    }
    if (typeof rec.description === 'string' && rec.description.trim()) {
      return rec.description.trim();
    }
    try {
      const json = JSON.stringify(rec);
      if (json && json !== '{}') return json;
    } catch {
      // ignore
    }
  }
  return fallback ?? 'Unknown error';
}

interface ApiOptions {
  method?: string;
  /**
   * JSON payload for the request body. jsonRequestInit owns its encoding
   * (serialization + Content-Type) on both the authenticated and public
   * paths. There is deliberately no raw headers/body escape hatch: when the
   * encoding rule was per-call-site, forgetting the header was harmless on
   * the authenticated path and silent breakage on the public one.
   */
  json?: unknown;
  public?: boolean;
  /**
   * Ask as the session itself even while the tab views as another reader (`utils/viewAs`): the user menu's
   * `/api/me`, whose answer is stored as the session's identity.
   */
  asSelf?: boolean;
}

/** Method defaults to POST when a body is present, GET otherwise. */
function toRequestInit(options?: ApiOptions): RequestInit {
  if (options?.json !== undefined) {
    return jsonRequestInit(options.method ?? 'POST', options.json);
  }
  return { method: options?.method };
}

async function fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
  let token = getAccessToken();
  if (!token || isTokenExpired(token)) {
    token = await refreshAccessToken();
    if (!token) {
      logout();
      throw new Error('Session expired');
    }
  }

  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${token}`);

  let response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    token = await refreshAccessToken();
    if (!token) {
      logout();
      throw new Error('Session expired');
    }
    headers.set('Authorization', `Bearer ${token}`);
    response = await fetch(url, { ...options, headers });
  }
  return response;
}

export async function apiJson<T = unknown>(url: string, options?: ApiOptions): Promise<T> {
  const init = options?.asSelf ? toRequestInit(options) : withViewAs(toRequestInit(options));
  const response = options?.public
    ? await fetch(url, init)
    : await fetchWithAuth(url, init);

  let data: unknown = null;
  let text: string | null = null;
  try {
    data = await response.json();
  } catch {
    try {
      text = await response.text();
    } catch {
      text = null;
    }
    data = null;
  }

  if (!response.ok) {
    const message = extractErrorMessage(data ?? text, `Request failed (${response.status})`);
    throw new ApiError(message, response.status, data ?? text);
  }

  return data as T;
}

/**
 * Open a streaming GET whose auth is optional: a signed-in reader's token is
 * sent (so per-reader filtering applies), a signed-out visitor's request goes
 * out public and is never bounced to /login. Rejects on a non-OK status with
 * the server's message; the caller reads ``response.body``.
 */
export async function apiStreamOptionalAuth(url: string, signal?: AbortSignal): Promise<Response> {
  const init = withViewAs({ method: 'GET', signal });
  const response = getAccessToken() ? await fetchWithAuth(url, init) : await fetch(url, init);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    throw new ApiError(extractErrorMessage(parsed ?? text, `Request failed (${response.status})`), response.status, parsed ?? text);
  }
  return response;
}
